import { useState, useRef, useCallback } from 'react';
import SwiperType from 'swiper';

interface UseSwiperSetupProps {
  onInit?: (swiper: SwiperType) => void;
  onSwiper?: (swiper: SwiperType) => void;
  withThumbs?: boolean;
}

interface UseSwiperSetupReturn {
  init: boolean;
  swiperRef: React.MutableRefObject<SwiperType | undefined>;
  navigationPrevRef: React.RefObject<HTMLDivElement>;
  navigationNextRef: React.RefObject<HTMLDivElement>;
  thumbsSwiper: SwiperType | undefined;
  handleInit: (swiper: SwiperType) => void;
  handleOnSwiper: (swiper: SwiperType) => void;
}

export const useSwiperSetup = ({ onInit, onSwiper, withThumbs }: UseSwiperSetupProps): UseSwiperSetupReturn => {
  const [init, setInit] = useState(false);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType>();
  const swiperRef = useRef<SwiperType>();
  const navigationPrevRef = useRef<HTMLDivElement>(null);
  const navigationNextRef = useRef<HTMLDivElement>(null);

  const setupResizeObserver = useCallback((swiper: SwiperType) => {
    if (typeof window === 'undefined' || !window.ResizeObserver) return;

    const resizeObserver = new ResizeObserver(() => {
      if (swiper && !swiper.destroyed) {
        swiper.update();
        swiper.updateSlides();
        swiper.updateProgress();
        swiper.updateSlidesClasses();
      }
    });

    const container = swiper.el.parentElement;
    if (container) {
      resizeObserver.observe(container);
      (swiper as any).resizeObserver = resizeObserver;
    }
  }, []);

  const handleInit = useCallback(
    (swiper: SwiperType) => {
      setInit(true);
      onInit?.(swiper);

      swiper.on('destroy', () => {
        if ((swiper as any).resizeObserver) {
          (swiper as any).resizeObserver.disconnect();
        }
      });
    },
    [onInit],
  );

  const handleOnSwiper = useCallback(
    (swiper: SwiperType) => {
      swiperRef.current = swiper;
      onSwiper?.(swiper);

      if (withThumbs) {
        setThumbsSwiper(swiper);
      }

      swiper.update();
      setupResizeObserver(swiper);
    },
    [onSwiper, withThumbs, setupResizeObserver],
  );

  return {
    init,
    swiperRef,
    navigationPrevRef,
    navigationNextRef,
    thumbsSwiper,
    handleInit,
    handleOnSwiper,
  };
};
